/**
 * The web app's screen models: the same results as the views above, in the shapes the screens
 * draw (numbers for charts, engine values for editors). Formatting stays in the app.
 */
import {
  assets as assetsNs, contract, core, debt as debtNs, formatDay, forecasting, parseDay, plan as planNs, schedule,
  type Asset, type Checkpoint, type Day, type Decimal, type GoalProgress, type Plan, type PlanForecast, type Series, type Transaction, type Wire,
} from '@map-my-money/engine';
import { milestones as canonicalMilestones } from './forecast.ts';
import { money, num } from './money.ts';
import { nowState } from './now.ts';
import { ownedAssets as ownedAssetsOf, type OverviewResult } from './overview.ts';
import { linePoints, monthFlows, yearFlows } from './line.ts';
import { paymentsBetween } from './flows.ts';
import { decode, planAsOf, runForecast, type Forecasted, type WaypointInput, type WirePlan } from './run.ts';

export const decimalString = (value: Decimal): string => contract.encodeDecimal(core.round2(value));

export const latest = <T>(items: readonly T[]): T | null => items[items.length - 1] ?? null;

/** The balances a new waypoint stores: the confirmed cash, investments and debts, the rest valued from the plan. */
export function waypointBalances(
  plan: Plan, cash: Decimal, investmentsValue: Decimal, date: Day, today: Day, debts: Readonly<Record<string, string>> = {},
): Wire.Balances {
  const debtBalances = new Map(Object.entries(debts).map(([id, balance]) => [id, core.abs(contract.decodeDecimal(balance))] as const));
  const snapshot = planNs.checkInBalances(plan, { cash, investments: investmentsValue, debtBalances }, date, { now: today });
  return {
    cash: decimalString(snapshot.cash),
    investments: decimalString(snapshot.investments),
    savings: decimalString(snapshot.savings),
    debt: decimalString(snapshot.debt),
    propertyEquity: decimalString(snapshot.propertyEquity),
    ...(snapshot.assetValue ? { assetValue: decimalString(snapshot.assetValue) } : {}),
  };
}

export const snapshotNetWorth = (c: Checkpoint): number => num(planNs.snapshotNetWorth(c.confirmedBalances));

// MARK: Forecast

export interface ChartRow {
  readonly date: Day;
  readonly cash: number;
  readonly investment: number;
  readonly equity: number;
  readonly debt: number;
  readonly netWorth: number;
}

export type SeriesKey = 'cash' | 'investment' | 'equity' | 'debt' | 'netWorth';

export interface Milestone {
  readonly id: string;
  readonly label: string;
  readonly date: Day;
  readonly series: SeriesKey;
  readonly value: number;
}

export interface CurrentState {
  readonly netWorth: number;
  readonly cash: number;
  readonly investments: number;
  readonly debt: number;
  readonly propertyEquity: number;
  readonly assetValue: number;
}

export interface CashflowYear {
  readonly year: number;
  readonly start: Day;
  readonly income: number;
  readonly expense: number;
  readonly debt: number;
  readonly investment: number;
}

export interface ForecastView {
  readonly years: number;
  readonly asOf: string;
  readonly forecast: PlanForecast;
  readonly rows: readonly ChartRow[];
  readonly current: CurrentState;
  readonly finalNetWorth: number;
  readonly goals: readonly GoalProgress[];
  readonly milestones: readonly Milestone[];
  readonly cashflowYears: readonly CashflowYear[];
  /** The month bars (in, out, kept, growth) by calendar year, from this month (R22). */
  readonly flowYears: readonly FlowYear[];
}

export interface FlowYear {
  readonly year: number;
  readonly from: Day;
  readonly partial: boolean;
  readonly in: number;
  readonly out: number;
  readonly kept: number;
  readonly growth: number;
  readonly netWorthChange: number;
}

/** The line's points as chart rows: now (dated today), then each month's end (R20, R22). */
const toRows = (f: Forecasted): ChartRow[] =>
  linePoints(f).map((p) => ({
    date: p.date,
    cash: num(p.cash),
    investment: num(p.investments),
    equity: num(p.equity),
    debt: num(p.debt),
    netWorth: num(p.netWorth),
  }));

const CHART_SERIES: Record<string, SeriesKey> = { netWorth: 'netWorth', cash: 'cash', investment: 'investment', debt: 'debt' };

/**
 * The forecast's milestones placed on the chart: the goal milestones of the series the chart
 * draws (passive income has no line, and the debt-free date the chart leaves to the key numbers),
 * in goal order, each at the row nearest its date.
 */
function chartMilestones(goals: readonly GoalProgress[], all: ReturnType<typeof canonicalMilestones>, rows: readonly ChartRow[]): Milestone[] {
  if (rows.length === 0) return [];
  const order = new Map(goals.map((g, i) => [g.goal.id, i]));
  return all
    .filter((m) => m.goal_id !== '' && CHART_SERIES[m.series] !== undefined)
    .sort((a, b) => (order.get(a.goal_id) ?? 0) - (order.get(b.goal_id) ?? 0))
    .map((m) => {
      const date = parseDay(m.date);
      const series = CHART_SERIES[m.series]!;
      const row = rows.reduce((best, r) => (Math.abs(r.date - date) < Math.abs(best.date - date) ? r : best), rows[0]!);
      return { id: m.goal_id, label: m.label, date, series, value: series === 'debt' ? 0 : row[series] };
    });
}

function sumByYear(series: Series, rows: Map<number, number>) {
  for (const b of series) {
    const year = core.toYmd(b.start).year;
    rows.set(year, (rows.get(year) ?? 0) + num(b.amount));
  }
}

function cashflowByYear(f: PlanForecast): CashflowYear[] {
  const payments = f.cashflow.payments;
  const pick = (series: Series) => {
    const m = new Map<number, number>();
    sumByYear(series, m);
    return m;
  };
  const income = pick(payments.income);
  const expense = pick(payments.expense);
  const debt = pick(payments.debt);
  const investment = pick(payments.investment);
  const first = f.cumulative.netWorth[0]?.start;
  const years = [...new Set(f.cumulative.netWorth.map((b) => core.toYmd(b.start).year))];
  return years.map((year) => ({
    year,
    start: first !== undefined && core.toYmd(first).year === year ? first : core.fromYmd(year, 1, 1),
    income: income.get(year) ?? 0,
    expense: expense.get(year) ?? 0,
    debt: debt.get(year) ?? 0,
    investment: investment.get(year) ?? 0,
  }));
}

export function forecastScreen(wire: WirePlan, waypoints: readonly WaypointInput[], asOf: string, years: number): ForecastView {
  const f = runForecast({ plan: wire, waypoints, asOf }, years);
  const rows = toRows(f);
  const now = nowState(f);
  return {
    years,
    asOf,
    forecast: f.forecast,
    rows,
    current: {
      netWorth: Number(money(now.netWorth)),
      cash: Number(money(now.cash)),
      investments: Number(money(now.investments)),
      debt: Number(money(now.debt)),
      propertyEquity: Number(money(now.propertyEquity)),
      assetValue: Number(money(now.assetValue)),
    },
    finalNetWorth: rows[rows.length - 1]?.netWorth ?? 0,
    goals: f.goals,
    milestones: chartMilestones(f.goals, canonicalMilestones(f), rows),
    cashflowYears: cashflowByYear(f.forecast),
    flowYears: yearFlows(monthFlows(f)).map((y) => ({
      year: y.year, from: y.from, partial: y.partial,
      in: num(y.in), out: num(y.out), kept: num(y.kept), growth: num(y.growth), netWorthChange: num(y.netWorthChange),
    })),
  };
}

// MARK: Home

/** Home's net worth and its parts, read from the overview result so Home and get_overview cannot differ. */
export const currentFromOverview = (o: OverviewResult): CurrentState => ({
  netWorth: Number(o.net_worth),
  cash: Number(o.breakdown.cash),
  investments: Number(o.breakdown.investments),
  debt: Number(o.breakdown.debt),
  propertyEquity: Number(o.breakdown.property_equity),
  assetValue: Number(o.breakdown.other_assets),
});

export const restOfMonthFromOverview = (o: OverviewResult) => ({
  total: Number(o.month_end.change_rest_of_month),
  cash: Number(o.month_end.cash_change),
  growth: Number(o.month_end.investment_growth),
});

export interface OwnedAsset {
  readonly id: string;
  readonly label: string;
  readonly assetClass: Asset['assetClass'];
  readonly value: number;
  readonly changePercent: number;
}

/**
 * What Home lists as owned: the overview's assets (same filter, same values) with the change since
 * bought unrounded, so the screen rounds it once (the overview carries it at 1 dp).
 */
export function ownedAssets(wire: WirePlan, today: Day): OwnedAsset[] {
  return ownedAssetsOf({ plan: decode(wire), today }).map((a) => {
    const value = Number(a.value);
    const price = Number(a.purchase_price);
    return { id: a.id, label: a.label, assetClass: a.asset_class, value, changePercent: price > 0 ? ((value - price) / price) * 100 : 0 };
  });
}

export interface MonthPoint {
  readonly date: Day;
  readonly value: number;
  readonly projected: boolean;
  readonly labels: readonly { readonly label: string; readonly amount: number; readonly type: string }[];
}

export interface MonthView {
  readonly periodStart: Day;
  readonly periodEnd: Day;
  readonly today: Day;
  readonly points: readonly MonthPoint[];
  readonly startValue: number;
  readonly todayValue: number;
  readonly endValue: number;
}

interface MonthEvent {
  readonly date: Day;
  /** Cash in positive, out negative. */
  readonly amount: number;
  readonly label: string;
  readonly type: Transaction['type'];
}

const monthEvents = (plan: Plan, from: Day, to: Day, today: Day): MonthEvent[] =>
  paymentsBetween(plan, from, to, today).map((e) => ({ date: parseDay(e.date), amount: (e.direction === 'in' ? 1 : -1) * Number(e.amount), label: e.label, type: e.kind }));

const groupByDay = (events: readonly MonthEvent[]): { date: Day; events: MonthEvent[] }[] => {
  const groups: { date: Day; events: MonthEvent[] }[] = [];
  for (const e of events) {
    const last = groups[groups.length - 1];
    if (last?.date === e.date) last.events.push(e);
    else groups.push({ date: e.date, events: [e] });
  }
  return groups;
};

const labelsOf = (events: readonly MonthEvent[]) => events.map((e) => ({ label: e.label, amount: e.amount, type: e.type }));

/**
 * Home's month card: this month's cash, split at today (R21). Payments dated up to today are already
 * inside now; the rest are still to come, and the card ends where the line's end of this month does.
 */
export function monthView(wire: WirePlan, waypoints: readonly WaypointInput[], today: Day, currentCash: number): MonthView {
  const plan = planAsOf(decode(wire), waypoints, formatDay(today));
  const period = forecasting.currentPeriod(today);
  const events = monthEvents(plan, period.start, period.end, today);
  const past = events.filter((e) => e.date <= today);
  const startValue = currentCash - past.reduce((total, e) => total + e.amount, 0);
  const points: MonthPoint[] = [];
  let value = startValue;
  if (period.start < today) points.push({ date: period.start, value, projected: false, labels: [] });
  for (const group of groupByDay(past.filter((e) => e.date < today))) {
    value += group.events.reduce((total, e) => total + e.amount, 0);
    points.push({ date: group.date, value, projected: false, labels: labelsOf(group.events) });
  }
  points.push({ date: today, value: currentCash, projected: false, labels: labelsOf(past.filter((e) => e.date === today)) });
  value = currentCash;
  for (const group of groupByDay(events.filter((e) => e.date > today))) {
    value += group.events.reduce((total, e) => total + e.amount, 0);
    points.push({ date: group.date, value, projected: true, labels: labelsOf(group.events) });
  }
  return {
    periodStart: period.start,
    periodEnd: period.end,
    today,
    points,
    startValue,
    todayValue: currentCash,
    endValue: points[points.length - 1]!.value,
  };
}

// MARK: Plan editor (each item's numbers: plan-editor.ts)

export interface MonthlyAmounts {
  readonly income: number;
  readonly out: number;
  readonly invested: number;
}

const monthlyAmounts = (plan: Plan): MonthlyAmounts => ({
  income: num(planNs.monthlyIncome(plan)),
  out: num(core.add(planNs.monthlyExpenses(plan), planNs.monthlyDebtPayments(plan))),
  invested: num(planNs.monthlyInvestments(plan)),
});

/** When an item starts counting: its own start (a loan's drawdown), or its linked asset's purchase if later (R23). */
function countsFrom(plan: Plan, t: Transaction): Day {
  const own = t.type === 'debt' ? core.maxDay(t.startDate, debtNs.effectiveStartDate(t)) : t.startDate;
  const asset = t.linkedAssetId === null ? undefined : plan.assets.find((a) => a.id === t.linkedAssetId);
  return asset === undefined ? own : core.maxDay(own, asset.purchaseDate);
}

/**
 * The plan's monthly in, out and invested for what has started by today, and the items that start
 * later, by the month they start: nothing counts before it exists (R23).
 */
export function monthlyTotals(wire: WirePlan, today: Day): MonthlyAmounts & { readonly later: readonly (MonthlyAmounts & { readonly from: Day })[] } {
  const plan = decode(wire);
  const byMonth = new Map<Day, Transaction[]>();
  const started: Transaction[] = [];
  for (const t of plan.transactions) {
    const from = countsFrom(plan, t);
    if (from <= today) started.push(t);
    else if (t.frequency !== 'oneTime') {
      const month = core.startOfMonth(from);
      byMonth.set(month, [...(byMonth.get(month) ?? []), t]);
    }
  }
  const later = [...byMonth.entries()]
    .sort(([a], [b]) => a - b)
    .map(([from, transactions]) => ({ from, ...monthlyAmounts({ ...plan, transactions }) }))
    .filter((m) => m.income !== 0 || m.out !== 0 || m.invested !== 0);
  return { ...monthlyAmounts({ ...plan, transactions: started }), later };
}

export const loanPayment = (principal: number, ratePercent: number, termMonths: number): number => {
  const p = debtNs.monthlyPayment({
    principal: core.fromDouble(principal),
    annualRatePercent: core.fromDouble(ratePercent),
    termMonths,
  });
  return p === null ? 0 : Math.round(num(p) * 100) / 100;
};

export const goalMonthsAway = (g: GoalProgress, today: Day): number | null =>
  g.estimatedDate === null ? null : Math.max(0, core.monthsBetween(today, g.estimatedDate));

/** A goal's date as every surface words it (R26): met now, on course for a month (and how far away), or not within the horizon. */
export type GoalWhen =
  | { readonly kind: 'reached' }
  | { readonly kind: 'onCourse'; readonly date: Day; readonly monthsAway: number }
  | { readonly kind: 'beyond' };

export function goalWhen(g: GoalProgress, today: Day): GoalWhen {
  if (g.isAchieved) return { kind: 'reached' };
  const months = goalMonthsAway(g, today);
  return g.estimatedDate === null || months === null ? { kind: 'beyond' } : { kind: 'onCourse', date: g.estimatedDate, monthsAway: months };
}

/** Whole years and months, for "1 year 3 months away". */
export const yearsAndMonths = (months: number): { readonly years: number; readonly months: number } => {
  const m = Math.max(0, Math.round(months));
  return { years: Math.floor(m / 12), months: m % 12 };
};
